<template>
    <form @submit.prevent="submitForm">
        <div v-if="error" class="error-message-gcn">{{ error }}</div>

        <p class="text-primary">Añadiendo registro para: <strong>{{ portfolio.name }}</strong></p>

        <!-- Transaction Type Selection -->
        <div class="form-group-gcn">
            <label class="text-primary">Tipo de Registro <span class="required">*</span></label>
            <div>
               <label class="radio-label text-secondary">
                   <input type="radio" v-model="formData.transaction_type" :value="0" @change="resetFieldsBasedOnType"> Balance Diario
               </label>
                <label class="radio-label text-secondary">
                   <input type="radio" v-model="formData.transaction_type" :value="1" @change="resetFieldsBasedOnType"> Aumento Capital
               </label>
                <label class="radio-label text-secondary">
                   <input type="radio" v-model="formData.transaction_type" :value="2" @change="resetFieldsBasedOnType"> Disminución Capital
               </label>
           </div>
        </div>

       <div class="form-group-gcn">
           <label for="balance-date" class="text-primary">Fecha <span class="required">*</span></label>
           <input 
               type="text" 
               id="balance-date" 
               class="form-control-gcn" 
               :class="{ 'error': shouldShowError('date') }"
               v-model="formData.date" 
               @input="formatDateInput" 
               @blur="markFieldTouched('date'); validateDate()"
               @focus="markFieldTouched('date')"
               required 
               placeholder="dd-mm-yyyy" 
               maxlength="10">
           <small class="text-secondary">Formato: día-mes-año (dd-mm-yyyy)</small>
           <small v-if="shouldShowError('date')" class="error-message-gcn">{{ dateError }}</small>
       </div>

        <div class="form-group-gcn">
           <label for="balance-description" class="text-primary">Descripción <span class="required">*</span></label>
           <input 
               type="text" 
               id="balance-description" 
               class="form-control-gcn" 
               :class="{ 'error': shouldShowError('description') }"
               v-model="formData.description" 
               @blur="markFieldTouched('description'); validateDescription()" 
               @input="validateDescriptionRealTime"
               @focus="markFieldTouched('description')"
               required 
               maxlength="255"
               placeholder="Ingrese una descripción">
           <div class="input-feedback">
               <small v-if="shouldShowError('description')" class="error-message-gcn">{{ descriptionError }}</small>
               <small v-else-if="formData.description" class="character-count text-secondary">
                   {{ formData.description.length }}/255 caracteres
               </small>
           </div>
        </div>

        <!-- Fields for Variation (Type 1 or 2) -->
        <template v-if="formData.transaction_type === 1 || formData.transaction_type === 2">
           <div class="form-group-gcn">
               <label for="balance-variation" class="text-primary">Importe Variación ({{ portfolio.currency }}) <span class="required">*</span></label>
               <input 
                   type="text" 
                   id="balance-variation" 
                   class="form-control-gcn"
                   :class="{ 'error': shouldShowError('variation_capital') }"
                   :value="formatDisplayValue(formData.variation_capital)"
                   @input="event => handleNumericInput(event, 'variation_capital')"
                   @blur="event => { markFieldTouched('variation_capital'); formatNumericOnBlur(event, 'variation_capital'); }"
                   @focus="event => { markFieldTouched('variation_capital'); handleFocus(event, 'variation_capital'); }"
                   placeholder="0.00" 
                   required>
                <small class="text-secondary">Introduce un valor positivo. El tipo de transacción determina si es aumento o disminución.</small>
                <small v-if="shouldShowError('variation_capital')" class="error-message-gcn">{{ variation_capital_error }}</small>
           </div>
        </template>

        <!-- Fields for Daily Balance (Type 0) -->
         <template v-if="formData.transaction_type === 0">
           <hr>
            <div class="form-group-gcn">
               <label for="balance-cash" class="text-primary">Capital Efectivo (Cash) <span class="required">*</span></label>
                <div class="input-group">
                    <input 
                        type="text" 
                        id="balance-cash" 
                        class="form-control-gcn"
                        :class="{ 'error': shouldShowError('cash') }"
                        :value="formatDisplayValue(formData.cash)"
                        @input="event => handleNumericInput(event, 'cash')"
                        @blur="event => { markFieldTouched('cash'); formatNumericOnBlur(event, 'cash'); }"
                        @focus="event => { markFieldTouched('cash'); handleFocus(event, 'cash'); }"
                        placeholder="0.00" 
                        required>
                </div>
                <small class="text-secondary">Introduce el efectivo. Los otros capitales se calcularán automáticamente.</small>
                <small v-if="shouldShowError('cash')" class="error-message-gcn">{{ cashAndCalculationError }}</small>
           </div>
           
            <div class="form-group-gcn calculated-field-container">
               <label for="balance-real" class="text-primary">Capital Real (Calculado)</label>
               <div class="input-wrapper">
                   <input type="text" id="balance-real" class="form-control-gcn calculated-field" :value="formatCalculatedValue(formData.real_capital)" readonly>
                   <div v-if="calculationLoading" class="field-loading-overlay">
                       <div class="field-spinner"></div>
                       <span class="loading-text">Calculando...</span>
                   </div>
               </div>
           </div>
           <div class="form-group-gcn calculated-field-container">
               <label for="balance-ficticious" class="text-primary">Capital Ficticio (Calculado)</label>
               <div class="input-wrapper">
                   <input type="text" id="balance-ficticious" class="form-control-gcn calculated-field" :value="formatCalculatedValue(formData.ficticious_capital)" readonly>
                   <div v-if="calculationLoading" class="field-loading-overlay">
                       <div class="field-spinner"></div>
                       <span class="loading-text">Calculando...</span>
                   </div>
               </div>
           </div>
        </template>

        <!-- Form Status Indicator -->
        <div v-if="isFormValid && submitAttempted" class="form-status success">
            <span class="status-icon">✓</span> Formulario completado correctamente
        </div>
        <div v-else-if="hasErrors && submitAttempted" class="form-status error">
            <span class="status-icon">⚠</span> Por favor, corrija los errores antes de continuar
        </div>

        <!-- Hidden submit button -->
        <button type="submit" style="display: none;"></button>
   </form>
</template>

<script>
import apiService, { formatDateDMY, parseDate } from './apiService';

export default {
   name: 'BalanceForm',
   props: {
       portfolio: { type: Object, required: true },
        error: { type: String, default: null },
        isSubmitting: { type: Boolean, default: false }
   },
   mounted() {
       // Run initial validation when component mounts
       this.$nextTick(() => {
           this.validateDateRealTime();
           this.validateDescriptionRealTime();
           
           if (this.formData.transaction_type === 1 || this.formData.transaction_type === 2) {
               this.triggerVariationCapitalWatcher(this.formData.variation_capital);
           } else {
               this.triggerCashWatcher(this.formData.cash);
           }
       });
   },
   data() {
       return {
            formData: {
               transaction_type: 1,
               date: formatDateDMY(new Date()),
               description: 'Aumento de capital',
               variation_capital: null,
               ficticious_capital: null,
               cash: null,
               real_capital: null,
           },
           calculationLoading: false,
           cashAndCalculationError: null,
           variation_capital_error: null,
           dateError: null,
           descriptionError: null,
           debounceTimer: null,
           // Validation states
           dateValid: false,
           descriptionValid: false,
           variationValid: false,
           cashValid: false,
           // Touch states
           touchedFields: {
               date: false,
               description: false,
               variation_capital: false,
               cash: false
           },
           submitAttempted: false
       };
   },
   computed: {
       isFormValid() {
           const baseValid = this.dateValid && this.descriptionValid;
           
           if (this.formData.transaction_type === 0) {
               const balanceValid = baseValid && this.cashValid && !this.cashAndCalculationError && 
                      this.formData.real_capital !== null && this.formData.ficticious_capital !== null;
               return balanceValid;
           } else {
               const variationModeValid = baseValid && this.variationValid && !this.variation_capital_error;
               return variationModeValid;
           }
       },
       hasErrors() {
           return !!(this.dateError || this.descriptionError || this.variation_capital_error || this.cashAndCalculationError);
       }
   },
   methods: {
        markFieldTouched(fieldName) {
            this.touchedFields[fieldName] = true;
        },

        shouldShowError(fieldName) {
            return (this.touchedFields[fieldName] || this.submitAttempted) && this[fieldName + 'Error'];
        },

        formatDateInput(event) {
            let input = event.target;
            let cursorPos = input.selectionStart;
            let oldValue = this.formData.date || '';
            let digits = input.value.replace(/\D/g, '');

            let newValue = '';
            if (digits.length > 4) {
                newValue = digits.slice(0, 2) + '-' + digits.slice(2, 4) + '-' + digits.slice(4, 8);
            } else if (digits.length > 2) {
                newValue = digits.slice(0, 2) + '-' + digits.slice(2, 4);
            } else {
                newValue = digits;
            }

            this.formData.date = newValue;
            this.validateDateRealTime();

            this.$nextTick(() => {
                let originalDigitCount = 0;
                for(let i = 0; i < cursorPos; i++) {
                    if (/\d/.test(oldValue[i])) {
                        originalDigitCount++;
                    }
                }

                let finalPos = 0;
                let currentDigitCount = 0;
                for(let i = 0; i < newValue.length; i++) {
                    finalPos = i + 1;
                    if (/\d/.test(newValue[i])) {
                        currentDigitCount++;
                    }
                    if (currentDigitCount >= originalDigitCount) {
                        if (cursorPos > 0 && oldValue.length > newValue.length && oldValue[cursorPos - 1] === '-' && !/\d/.test(newValue[finalPos-1])) {
                           finalPos--;
                        }
                        break; 
                    }
                }
                if (originalDigitCount === 0) finalPos = 0;
                finalPos = Math.max(0, Math.min(finalPos, newValue.length));

                input.selectionStart = finalPos;
                input.selectionEnd = finalPos;
            });
        },

        validateDate() {
            this.validateDateRealTime();
        },

        validateDateRealTime() {
            if (!this.formData.date) {
                this.dateError = 'La fecha es requerida.';
                this.dateValid = false;
                return;
            }

            // Normalize date (add leading zeros)
            const parts = this.formData.date.split('-');
            if (parts.length === 3) {
                parts[0] = parts[0].padStart(2, '0');
                parts[1] = parts[1].padStart(2, '0');
                const normalizedDate = parts.join('-');
                if (normalizedDate !== this.formData.date) {
                    this.formData.date = normalizedDate;
                }
            }

            const dateRegex = /^\d{2}-\d{2}-\d{4}$/;
            if (!dateRegex.test(this.formData.date)) {
                this.dateError = 'La fecha debe estar en formato dd-mm-yyyy.';
                this.dateValid = false;
                return;
            }

            const parsedDate = parseDate(this.formData.date);
            if (!parsedDate) {
                this.dateError = 'La fecha ingresada no es válida.';
                this.dateValid = false;
                return;
            }

            // Check if date is not in the future
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            if (parsedDate > today) {
                this.dateError = 'La fecha no puede ser futura.';
                this.dateValid = false;
                return;
            }

            this.dateError = null;
            this.dateValid = true;
        },

        validateDescription() {
            this.validateDescriptionRealTime();
        },

        validateDescriptionRealTime() {
            if (!this.formData.description?.trim()) {
                this.descriptionError = 'La descripción es requerida.';
                this.descriptionValid = false;
            } else if (this.formData.description.length > 255) {
                this.descriptionError = 'La descripción no puede exceder 255 caracteres.';
                this.descriptionValid = false;
            } else {
                this.descriptionError = null;
                this.descriptionValid = true;
            }
        },

        formatDisplayValue(value) {
            if (value === null || value === undefined) return '';
            if (typeof value === 'number') {
                return value.toFixed(2);
            }
            return value;
        },

        handleFocus(event, modelKey) {
            // Show raw numeric value on focus for easier editing
            if (this.formData[modelKey] !== null && this.formData[modelKey] !== undefined) {
                event.target.value = this.formData[modelKey].toString();
            }
        },

       resetFieldsBasedOnType() {
            this.cashAndCalculationError = null;
            this.variation_capital_error = null;
            
            // Reset validation states properly based on transaction type
            if (this.formData.transaction_type === 0) {
                // Balance Diario mode - variation not needed, cash is needed
                this.variationValid = true; // Not relevant for balance mode
                this.cashValid = false; // Will be validated when cash is entered
                this.formData.variation_capital = null;
                this.formData.description = 'Balance diario';
            } else {
                // Variation mode - cash not needed, variation is needed
                this.variationValid = false; // Will be validated when variation is entered
                this.cashValid = true; // Not relevant for variation mode
                this.formData.ficticious_capital = null;
                this.formData.cash = null;
                this.formData.real_capital = null;
                this.formData.description = this.formData.transaction_type === 1 ? 'Aumento de capital' : 'Disminución de capital';
            }
            
            // Reset touch states for fields that are changing
            this.touchedFields.variation_capital = false;
            this.touchedFields.cash = false;
            
            this.validateDescriptionRealTime();
        },
        
        async calculateCapitals() {
            const cashValue = parseFloat(this.formData.cash);
            
            if (cashValue === null || cashValue === undefined || isNaN(cashValue)) {
                 this.formData.real_capital = null;
                 this.formData.ficticious_capital = null;
                 this.cashAndCalculationError = null;
                 this.calculationLoading = false;
                 this.$emit('calculation-loading', false);
                 return;
            }
            if (cashValue < 0) {
                this.formData.real_capital = null;
                this.formData.ficticious_capital = null;
                this.cashAndCalculationError = "El Capital Efectivo no puede ser negativo.";
                this.calculationLoading = false;
                this.$emit('calculation-loading', false);
                return;
            }

            this.calculationLoading = true;
            this.$emit('calculation-loading', true);
            this.cashAndCalculationError = null;
            
            try {
                const [realCap, fictCap] = await Promise.all([
                    apiService.calculateRealCapital(this.portfolio.idc, cashValue),
                    apiService.calculateFictitiousCapital(this.portfolio.idc, cashValue)
                ]);
                this.formData.real_capital = realCap;
                this.formData.ficticious_capital = fictCap;
            } catch (err) {
                 console.error("Error calculating capitals:", err);
                 this.cashAndCalculationError = `Error calculando capitales: ${err.message}`;
                  this.formData.real_capital = null;
                  this.formData.ficticious_capital = null;
            } finally {
                this.calculationLoading = false;
                this.$emit('calculation-loading', false);
            }
        },
       
       submitForm() {
            this.submitAttempted = true;
            
            // Mark all relevant fields as touched
            this.markFieldTouched('date');
            this.markFieldTouched('description');
            if (this.formData.transaction_type === 0) {
                this.markFieldTouched('cash');
            } else {
                this.markFieldTouched('variation_capital');
            }
            
            // Validate all fields
            this.validateDate();
            this.validateDescription();
            
            if (this.formData.transaction_type === 1 || this.formData.transaction_type === 2) {
                this.triggerVariationCapitalWatcher(this.formData.variation_capital);
            } else {
                // For balance mode, only validate cash but don't trigger recalculation during submit
                this.validateCashRealTime();
            }

            // Debug logging to help identify validation issues
            console.log('Form validation state:', {
                isFormValid: this.isFormValid,
                hasErrors: this.hasErrors,
                dateValid: this.dateValid,
                descriptionValid: this.descriptionValid,
                variationValid: this.variationValid,
                cashValid: this.cashValid,
                dateError: this.dateError,
                descriptionError: this.descriptionError,
                variation_capital_error: this.variation_capital_error,
                cashAndCalculationError: this.cashAndCalculationError,
                formData: this.formData
            });

            if (!this.isFormValid || this.hasErrors) {
                this.$emit('error', 'Por favor, corrija los errores en el formulario antes de continuar.');
                return;
            }

            let dataToSend = {
                transaction_type: this.formData.transaction_type,
                date: this.formData.date,
                description: this.formData.description,
            };

            if (this.formData.transaction_type === 0) {
                const cashValue = parseFloat(this.formData.cash);
                if (this.formData.ficticious_capital === null || this.formData.real_capital === null) {
                     this.$emit('error', 'Los capitales Real y Ficticio deben ser calculados. Verifique el Capital Efectivo e inténtelo de nuevo.'); 
                     return;
                }
                dataToSend.ficticious_capital = this.formData.ficticious_capital;
                dataToSend.cash = cashValue;
                dataToSend.real_capital = this.formData.real_capital;
            } else {
                const variationValue = parseFloat(this.formData.variation_capital);
                dataToSend.variation_capital = variationValue;
            }

            this.$emit('submit', dataToSend);
       },
       
               handleNumericInput(event, modelKey) {
            let value = event.target.value;
            // Only allow digits, dots, and commas (no dashes/minus signs)
            let sanitizedValue = value.replace(/[^\d.,]/g, '');
            
            // Handle decimal separators: find the last comma or dot as decimal separator
            const lastCommaIndex = sanitizedValue.lastIndexOf(',');
            const lastDotIndex = sanitizedValue.lastIndexOf('.');
            const lastDecimalIndex = Math.max(lastCommaIndex, lastDotIndex);
            
            if (lastDecimalIndex !== -1) {
                // Split into integer and decimal parts
                const integerPart = sanitizedValue.substring(0, lastDecimalIndex).replace(/[.,]/g, ''); // Remove all dots/commas from integer part
                const decimalPart = sanitizedValue.substring(lastDecimalIndex + 1);
                
                // Limit decimal part to 2 digits
                const limitedDecimalPart = decimalPart.substring(0, 2);
                
                // Reconstruct with dot as decimal separator
                // Always include the dot if there was a decimal separator, even if decimal part is empty
                sanitizedValue = integerPart + '.' + limitedDecimalPart;
            }
            
            // Prevent starting with decimal point - require leading zero
            if (sanitizedValue.startsWith('.')) {
                sanitizedValue = '0' + sanitizedValue;
            }

            event.target.value = sanitizedValue;

            if (sanitizedValue === '') {
                this.formData[modelKey] = null;
            } else if (!isNaN(parseFloat(sanitizedValue)) && isFinite(sanitizedValue)) {
                this.formData[modelKey] = sanitizedValue;
            } else if (sanitizedValue.endsWith('.')) {
                this.formData[modelKey] = sanitizedValue;
            } else {
                this.formData[modelKey] = null;
            }

            // Real-time validation
            if (modelKey === 'cash') {
                this.validateCashRealTime();
            } else if (modelKey === 'variation_capital') {
                this.validateVariationRealTime();
            }
        },

        validateCashRealTime() {
            const cashValue = parseFloat(this.formData.cash);
            if (isNaN(cashValue) || cashValue === null || cashValue === undefined) {
                this.cashValid = false;
                this.cashAndCalculationError = "El capital efectivo es requerido.";
                return;
            }
            
            if (cashValue < 0) {
                this.cashValid = false;
                this.cashAndCalculationError = "El capital efectivo no puede ser negativo.";
                return;
            }
            
            this.cashValid = true;
            this.cashAndCalculationError = null;
        },

        validateVariationRealTime() {
            const variationValue = parseFloat(this.formData.variation_capital);
            if (isNaN(variationValue) || variationValue === null || variationValue === undefined) {
                this.variationValid = false;
                return;
            }
            
            this.variationValid = variationValue >= 0;
        },

        formatNumericOnBlur(event, modelKey) {
            let value = this.formData[modelKey];
            const originalValue = value;
            
            if (value === null || String(value).trim() === '') {
                // Only update and trigger if the original value wasn't already null
                if (originalValue !== null) {
                    this.formData[modelKey] = null;
                    if (modelKey === 'variation_capital') {
                        this.triggerVariationCapitalWatcher(null);
                    } else if (modelKey === 'cash') {
                        this.triggerCashWatcher(null);
                    }
                }
                return;
            }
            
            if(typeof value === 'string' && value.endsWith('.')) {
                value = value.slice(0, -1);
            }

            const numericValue = parseFloat(value);
            if (!isNaN(numericValue) && isFinite(numericValue)) {
                // Round to 2 decimal places
                const roundedValue = Math.round(numericValue * 100) / 100;
                
                // Compare actual numeric values to detect real changes
                const originalNumericValue = parseFloat(originalValue);
                const actuallyChanged = isNaN(originalNumericValue) || originalNumericValue !== roundedValue;
                
                // Only update and trigger if the value actually changed
                if (actuallyChanged) {
                    this.formData[modelKey] = roundedValue;
                    if (modelKey === 'variation_capital') {
                        this.triggerVariationCapitalWatcher(this.formData.variation_capital);
                    } else if (modelKey === 'cash') {
                        this.triggerCashWatcher(this.formData.cash);
                    }
                }
                // If not changed, don't assign anything - this prevents Vue watcher from firing
            } else {
                // Value became invalid - only update if it wasn't already null
                if (originalValue !== null) {
                    this.formData[modelKey] = null;
                    if (modelKey === 'variation_capital') {
                        this.triggerVariationCapitalWatcher(this.formData.variation_capital);
                    } else if (modelKey === 'cash') {
                        this.triggerCashWatcher(this.formData.cash);
                    }
                }
            }
        },

        triggerVariationCapitalWatcher(numericNewVal) {
            // Only validate if we're in variation mode (type 1 or 2)
            if (this.formData.transaction_type === 1 || this.formData.transaction_type === 2) {
                if (numericNewVal === null || numericNewVal === undefined) {
                    this.variation_capital_error = "El importe de variación es requerido.";
                    this.variationValid = false;
                } else if (numericNewVal < 0) {
                    this.variation_capital_error = "El importe no puede ser negativo.";
                    this.variationValid = false;
                } else {
                    this.variation_capital_error = null;
                    this.variationValid = true;
                }
            } else {
                // Clear validation for non-variation types
                this.variation_capital_error = null;
                this.variationValid = true; // Not relevant for other transaction types
            }
        },
        
        triggerCashWatcher(newCash) {
            if (this.formData.transaction_type === 0) {
                if (this.debounceTimer) clearTimeout(this.debounceTimer);
                this.cashAndCalculationError = null;

                if (newCash === null || newCash === undefined || String(newCash).trim() === '') {
                    this.cashAndCalculationError = "El capital efectivo es requerido.";
                    this.calculationLoading = false;
                    this.$emit('calculation-loading', false);
                    this.cashValid = false;
                    this.formData.real_capital = null;
                    this.formData.ficticious_capital = null;
                    return;
                }
                if (newCash < 0) {
                    this.cashAndCalculationError = "El capital efectivo no puede ser negativo.";
                    this.calculationLoading = false;
                    this.$emit('calculation-loading', false);
                    this.cashValid = false;
                    this.formData.real_capital = null;
                    this.formData.ficticious_capital = null;
                    return;
                }
                
                this.cashValid = true;
                
                // Set loading state immediately when starting debounce
                this.calculationLoading = true;
                this.$emit('calculation-loading', true);
                
                this.debounceTimer = setTimeout(() => {
                    this.calculateCapitals();
                }, 750);
            }
        },
        
        formatCalculatedValue(value) {
            if (value === null || value === undefined) {
                return '';
            }
            return value.toFixed(2);
        }
   },
   watch: {
       portfolio: {
           immediate: true,
           handler(newVal, oldVal) {
               if (!oldVal || newVal.idc !== oldVal.idc) {
                   this.formData = {
                       transaction_type: 1, date: formatDateDMY(new Date()), description: 'Aumento de capital',
                       variation_capital: null, ficticious_capital: null, cash: null, real_capital: null,
                   };
                    this.cashAndCalculationError = null;
                    this.variation_capital_error = null;
                    this.dateError = null;
                    this.descriptionError = null;
                    this.dateValid = false;
                    this.descriptionValid = false;
                    this.variationValid = false;
                    this.cashValid = false;
                    this.submitAttempted = false;
                    
                    // Reset touch states
                    this.touchedFields = {
                        date: false,
                        description: false,
                        variation_capital: false,
                        cash: false
                    };
                    
                    // Validate initial values
                    this.validateDescriptionRealTime();
                    this.validateDateRealTime();
                    
                    // Initialize validation for the default transaction type
                    if (this.formData.transaction_type === 1 || this.formData.transaction_type === 2) {
                        this.triggerVariationCapitalWatcher(this.formData.variation_capital);
                    } else {
                        this.triggerCashWatcher(this.formData.cash);
                    }
               }
           }
       },
       'formData.cash': function(newCashOrString, oldCashOrString) {
           if (this.formData.transaction_type === 0) {
                let numericNewCash;
                if (typeof newCashOrString === 'string') {
                    if (newCashOrString.endsWith('.')) {
                        this.cashAndCalculationError = null; 
                        return; 
                    }
                    numericNewCash = parseFloat(newCashOrString);
                    if (isNaN(numericNewCash)) numericNewCash = null;
                } else {
                    numericNewCash = newCashOrString;
                }
                this.triggerCashWatcher(numericNewCash);
           }
       },
        'formData.variation_capital': function(newValOrString) {
            if (this.formData.transaction_type === 1 || this.formData.transaction_type === 2) {
                let numericNewVal;
                 if (typeof newValOrString === 'string') {
                    if (newValOrString.endsWith('.')) {
                         this.variation_capital_error = null;
                        return; 
                    }
                    numericNewVal = parseFloat(newValOrString);
                    if (isNaN(numericNewVal)) numericNewVal = null;
                } else {
                    numericNewVal = newValOrString;
                }
                this.triggerVariationCapitalWatcher(numericNewVal);
            } else {
                this.variation_capital_error = null;
            }
        },
        calculationLoading: function(newVal) {
            console.log('calculationLoading changed to:', newVal);
        }
   }
}
</script>

<style scoped>
.input-group { display: flex; }
.input-group .form-control-gcn { flex-grow: 1; }
.input-group-append { margin-left: -1px; }
.input-group-append .btnGcn { border-top-left-radius: 0; border-bottom-left-radius: 0; }
.loading-indicator-gcn.small { font-size: 0.9em; text-align: left; padding: 5px 0; }
hr { margin: 20px 0; border-color: #eee; }

/* Text color classes */
.text-primary {
    color: var(--color-text-primary);
}
.text-secondary {
    color: var(--color-text-secondary);
}
.text-inactive {
    color: var(--color-text-inactive);
}
.text-link {
    color: var(--color-text-link);
}
.error-message-gcn {
    color: var(--color-error);
    font-size: 0.8em;
    display: block;
    margin-top: 4px;
}
.radio-label {
    margin-right: 15px; 
    font-weight: normal;
}

/* Form inputs should have proper text color */
input, select, textarea {
    color: var(--color-text-primary);
}

/* Override for form control focus */
.form-control-gcn:focus {
    color: var(--color-text-primary);
}

/* Remove arrows from number inputs */
input[type=number]::-webkit-outer-spin-button,
input[type=number]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
input[type=number] {
    -moz-appearance: textfield;
}

/* Style calculated fields */
.calculated-field {
    background-color: #f8f9fa !important;
    color: #6c757d !important;
    cursor: not-allowed;
}

/* Input wrapper for positioning overlay */
.input-wrapper {
    position: relative;
}

/* Field loading overlay */
.field-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.95);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    z-index: 10;
    gap: 8px;
}

.field-spinner {
    width: 20px;
    height: 20px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid var(--color-highlight, #007bff);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

.loading-text {
    font-size: 0.8em;
    color: var(--color-text-secondary, #666);
    font-weight: 500;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

/* Enhanced validation styles */
.required {
    color: var(--color-error);
    font-weight: bold;
}

.form-control-gcn.error {
    border-color: var(--color-error);
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.input-feedback {
    margin-top: 4px;
}

.character-count {
    float: right;
    font-size: 0.75em;
}

.form-status {
    padding: 10px;
    border-radius: 4px;
    margin-top: 15px;
    font-size: 0.9em;
    display: flex;
    align-items: center;
    gap: 8px;
}

.form-status.success {
    background-color: #d4edda;
    border: 1px solid #c3e6cb;
    color: #155724;
}

.form-status.error {
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    color: #721c24;
}

.status-icon {
    font-weight: bold;
    font-size: 1.1em;
}
</style>